/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Bounds, PanState, Pan } from '../diagram.types';
export type PanBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/**
 * The pan values that describes where the diagram content
 * is panned
 */
export type PanningState = {
  panX: number;
  panY: number;
};

const constrainPan = (
  previousPan: PanningState,
  nextPan: PanningState,
  panBounds: PanBounds,
  panDirection: 'x' | 'y' | 'any'
) => {
  const panX = panDirection !== 'y' ? nextPan.panX : previousPan.panX;
  const panY = panDirection !== 'x' ? nextPan.panY : previousPan.panY;
  return {
    panX: panX > panBounds.maxX ? panBounds.maxX : panX < panBounds.minX ? panBounds.minX : panX,
    panY: panY > panBounds.maxY ? panBounds.maxY : panY < panBounds.minY ? panBounds.minY : panY
  };
};

export const pan = (
  previousPan: PanningState,
  nextPan: PanningState,
  panDirection: 'x' | 'y' | 'any',
  zoom: number,
  width: number,
  height: number,
  panBounds?: PanBounds,
  onPanChange?: (detail: PanState) => void
) => {
  if (!panBounds) {
    //On initial render before nodes are rendered
    return { newPan: { panX: 0, panY: 0 } };
  }
  const newPan = constrainPan(previousPan, nextPan, panBounds, panDirection);
  const isPanChanged = newPan.panX !== previousPan.panX || newPan.panY !== previousPan.panY;
  const center = panXYToCenterXY(newPan.panX, newPan.panY, zoom, width, height);
  isPanChanged &&
    onPanChange &&
    onPanChange({
      centerX: center.centerX,
      centerY: center.centerY
    });
  return { newPan };
};

export const getPanBounds = (
  dWidth: number,
  dHeight: number,
  elemBounds: Bounds,
  zoom: number,
  minZoom: number,
  initPanZoomState: {
    zoom: number;
    centerX?: number;
    centerY?: number;
  },
  panType?: string
): PanBounds => {
  const { x, y, width, height } = elemBounds;
  let minX, maxX, minY, maxY;
  if (panType === 'centerContent') {
    minX = dWidth / 2 - (width + x) * zoom;
    minY = dHeight / 2 - (height + y) * zoom;
    maxX = dWidth / 2 - x * zoom;
    maxY = dHeight / 2 - y * zoom;
  } else {
    // panType equals fixed
    const zoomRatio = zoom / minZoom;
    // Find left corner of the content at min zoom and content is centered
    const minZoomX = (dWidth - width * minZoom) / 2 - x * minZoom;
    const minZoomY = (dHeight - height * minZoom) / 2 - y * minZoom;
    // When we pan all the way to the right, the left corner of displayable area is 0,0 as in min zoom.
    // When we pan all the way to the left, lets find the corner of the displayable area
    const leftCornerX = dWidth - dWidth * zoomRatio;
    const leftCornerY = dHeight - dHeight * zoomRatio;
    // Now we have everything to calculate pan zoom constraints
    minX = leftCornerX + minZoomX * zoomRatio;
    minY = leftCornerY + minZoomY * zoomRatio;
    maxX = minZoomX * zoomRatio;
    maxY = minZoomY * zoomRatio;
  }
  const initZoom = initPanZoomState.zoom;
  const initCenterX = initPanZoomState.centerX;
  const initCenterY = initPanZoomState.centerY;
  // If an initial panZoomState is set, should expand pan bounds to include it
  let dx, dy;
  if (initCenterX) {
    const boundsX = initCenterX * initZoom - dWidth / 2;
    if (-boundsX * initZoom < minX) {
      dx = minX - boundsX * initZoom;
      minX -= dx;
      maxX += dx;
    } else if (-boundsX * initZoom > maxX) {
      dx = -boundsX * initZoom - maxX;
      minX -= dx;
      maxX += dx;
    }
  }
  if (initCenterY) {
    const boundsY = initCenterY * initZoom - dHeight / 2;
    if (-boundsY * initZoom < minY) {
      dy = minY - boundsY * initZoom;
      minY -= dy;
      maxY += dy;
    } else if (-boundsY * initZoom > maxY) {
      dy = -boundsY * initZoom - maxY;
      minY -= dy;
      maxY += dy;
    }
  }
  return {
    minX: minX,
    maxX: maxX,
    minY: minY,
    maxY: maxY
  };
};

/**
 * Returns the pan properties for the diagram.
 */
export function getPanProps(
  props: Pan,
  contentBounds: Bounds,
  userDragMode: string | undefined,
  isDeviceTouch: boolean
) {
  return {
    panning: isDeviceTouch
      ? userDragMode === 'pan' && props.panning
        ? props.panning
        : 'off'
      : props.panning
      ? props.panning
      : 'off',
    panDirection: props.panDirection ? props.panDirection : 'any',
    centerX: props.centerX !== undefined ? props.centerX : contentBounds.width / 2,
    centerY: props.centerY !== undefined ? props.centerY : contentBounds.height / 2,
    onPan: props.onPan
  };
}

/**
 * Converts centerX,Y coordinates to panX,Y coordinates
 */
export function centerXYToPanXY(
  centerX: number,
  centerY: number,
  zoom: number,
  width: number,
  height: number
) {
  return {
    panX: width / 2 - centerX * zoom,
    panY: height / 2 - centerY * zoom
  };
}
/**
 * Converts panX,Y coordinates to centerX,Y coordinates
 */
export function panXYToCenterXY(
  panX: number,
  panY: number,
  zoom: number,
  width: number,
  height: number
) {
  return {
    centerX: (width / 2 - panX) / zoom,
    centerY: (height / 2 - panY) / zoom
  };
}
